import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ADMIN_API } from "../../../common/common";
import { Logout } from "../../../common/Funcation";
import Loader from "../../../components/Loader";
import BettingProfitLoss from "../../../components/Profile/BettingProfitLoss";
import { postApi } from "../../../service";

const AGENT_LEVEL_BG: Record<string, string> = {
  O: "#c43434",
  SUO: "#c5c81f",
  WL: "#d71990",
  SA: "#9f19d7",
  A: "#D77319",
  SUA: "#D65D5D",
  SS: "#85B352",
  S: "#568BC8",
  M: "#A762B5",
  CL: "#85B352",
  PL: "#2789ce",
};

const BET_TABS = [
  { key: "exchange", label: "Exchange" },
  { key: "sportsBook", label: "FancyBet" },
  { key: "fancy1Bet", label: "Fancy1Bet" },
  { key: "toss", label: "TossBet" },
  { key: "bookMark", label: "BookMaker" },
  { key: "premium", label: "Premium" },
  { key: "other", label: "Other" },
];

function extractBetRows(response: any): HistoryRow[] {
  const payload = response?.data?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.bets)) return payload.bets;
  if (Array.isArray(payload?.result)) return payload.result;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}

function resolvePlayerId(value: unknown): string {
  if (!value) return "";
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  if (typeof value === "object") {
    const obj = value as { _id?: string; id?: string; userId?: string };
    return String(obj._id || obj.id || obj.userId || "");
  }
  return "";
}

interface HistoryRow {
  _id: string;
  matchId: string;
  type: string;
  betType: string;
  betDisplayId: string;
  betSide: string;
  selection: string;
  subSelection: string;
  betId: number;
  stake: number;
  oddsUp: number;
  createdAt: Date;
  name: string;
  tType: string;
  profit: number;
  exposure: number;
  plId?: string;
  whoAdd?: any[];
  userId?: {
    whoAdd?: any[];
    uperLineInfo?: any[];
  };
}

interface CrumbUser {
  _id: string;
  user_name: string;
  agent_level: string;
}

function fmtPL(n: number, isLoss: boolean) {
  const v = Math.abs(Number(n) || 0);
  const signed = isLoss ? -v : v;
  return {
    type: signed >= 0 ? "text-green" : "text-danger",
    display: `(${signed.toFixed(2)})`,
  };
}

const LEVEL_ORDER = [
  "O",
  "SUO",
  "WL",
  "SA",
  "A",
  "SUA",
  "SS",
  "S",
  "M",
  "CL",
  "PL",
];

function getLevelRank(level?: string) {
  if (!level) return 999;
  const idx = LEVEL_ORDER.indexOf(String(level).toUpperCase());
  return idx === -1 ? 999 : idx;
}

function buildBreadcrumbChain(
  upline: any[],
  current: { _id: string; user_name: string; agent_level: string }
): CrumbUser[] {
  const chain: CrumbUser[] = [];
  const seen = new Set<string>();

  (upline || []).forEach((item: any) => {
    if (!item) return;
    const id = String(item._id || item.userId || "");
    const name = item.user_name || item.userName || "";
    const level = item.agent_level || item.agentLevel || "";
    if (!name) return;
    const key = `${id}_${name}`.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    chain.push({
      _id: id,
      user_name: name,
      agent_level: level,
    });
  });

  const currKey = `${current._id}_${current.user_name}`.toLowerCase();
  if (!seen.has(currKey) && current.user_name) {
    chain.push({
      _id: current._id,
      user_name: current.user_name,
      agent_level: current.agent_level || "PL",
    });
  }

  chain.sort((a, b) => {
    const rankA = getLevelRank(a.agent_level);
    const rankB = getLevelRank(b.agent_level);
    if (rankA !== rankB) return rankA - rankB;
    return 0;
  });

  return chain;
}

function RiskPlayerBetsPopup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = resolvePlayerId(searchParams.get("userId") || "");
  const userNameParam = searchParams.get("userName") || "";
  const agentLevelParam = searchParams.get("agentLevel") || "PL";

  const whoAddParamRaw = searchParams.get("whoAdd");
  const initialCrumbs = useMemo(() => {
    if (!whoAddParamRaw) {
      return [
        {
          _id: userId,
          user_name: userNameParam,
          agent_level: agentLevelParam || "PL",
        },
      ];
    }
    try {
      const parsed = JSON.parse(whoAddParamRaw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return buildBreadcrumbChain(parsed, {
          _id: userId,
          user_name: userNameParam,
          agent_level: agentLevelParam || "PL",
        });
      }
    } catch {
      // ignore
    }
    return [
      {
        _id: userId,
        user_name: userNameParam,
        agent_level: agentLevelParam || "PL",
      },
    ];
  }, [whoAddParamRaw, userId, userNameParam, agentLevelParam]);

  const [mainTab, setMainTab] = useState<"bethistory" | "profitloss">(
    "bethistory"
  );
  const [currentTab, setCurrentTab] = useState("exchange");
  const [betStatus, setBetStatus] = useState("unmatched");
  const [startDate, setStartDate] = useState(() =>
    moment().startOf("day").minute(1).toDate()
  );
  const [endDate, setEndDate] = useState(new Date());
  const [startTime, setStartTime] = useState("00:01");
  const [endTime, setEndTime] = useState(moment().format("HH:mm"));
  const [filterTab, setFilterTab] = useState("all");
  const [pageData, setPageData] = useState<HistoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [crumbs, setCrumbs] = useState<CrumbUser[]>(initialCrumbs);

  useEffect(() => {
    document.title = `${userNameParam || "Player"} - Bets History`;
  }, [userNameParam]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    const loadProfile = async () => {
      let foundUpline: any[] | null = null;

      // 1. Try search user by downline to fetch full whoAdd hierarchy
      if (userNameParam) {
        try {
          const res = await postApi({
            api: ADMIN_API.SETTING.USERS.GET_USER_LIST_BY_DOWNLINE,
            value: { search: userNameParam },
          });
          const list = res.data?.data?.userList;
          if (Array.isArray(list) && list.length > 0) {
            const match =
              list.find(
                (u: any) =>
                  String(u._id) === userId ||
                  String(u.user_name).toLowerCase() ===
                  userNameParam.toLowerCase()
              ) || list[0];
            const upline = match?.whoAdd || match?.uperLineInfo;
            if (Array.isArray(upline) && upline.length > 0) {
              foundUpline = upline;
            }
          }
        } catch {
          // ignore
        }
      }

      // 2. Try PLAYER_GET_PROFILE if not found
      if (!foundUpline && userId) {
        try {
          const res = await postApi({
            api: ADMIN_API.PLAYER_GET_PROFILE,
            value: { id: userId },
          });
          const data = res.data?.data;
          const upline = data?.whoAdd || data?.uperLineInfo;
          if (Array.isArray(upline) && upline.length > 0) {
            foundUpline = upline;
          }
        } catch {
          // ignore
        }
      }

      // 3. Try AGENT_GET_LIST if still not found
      if (!foundUpline && userId) {
        try {
          const res = await postApi({
            api: ADMIN_API.AGENT_GET_LIST,
            value: { userId, status: "all" },
          });
          const data = res.data?.data;
          const upline = data?.uperLineInfo || data?.whoAdd;
          if (Array.isArray(upline) && upline.length > 0) {
            foundUpline = upline;
          }
        } catch {
          // ignore
        }
      }

      if (cancelled) return;

      if (foundUpline && foundUpline.length > 0) {
        const fullChain = buildBreadcrumbChain(foundUpline, {
          _id: userId,
          user_name: userNameParam,
          agent_level: agentLevelParam || "PL",
        });
        setCrumbs(fullChain);
      }
    };

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [userId, userNameParam, agentLevelParam]);

  const getPageData = async (
    filterType: string = "",
    betType: string = currentTab,
    status: string = betStatus
  ) => {
    if (!userId) return;
    setLoading(true);
    try {
      // Top 20 amount/exposure is live matched exposure. "Matched" must
      // load running/pending bets, not settled history.
      const isMatched = status === "matched";
      const response = await postApi({
        api: ADMIN_API.PLAYER_RISK_BET_HISTORY,
        value: {
          id: userId,
          bet: isMatched ? "active" : "history",
          betStatus: isMatched ? "matched" : status,
          betType,
          filter: filterType === "" ? filterTab : filterType,
          from: moment(startDate).toISOString(),
          to: moment(endDate).toISOString(),
          page: 1,
          limit: 100,
        },
      });
      const rows = extractBetRows(response);
      setPageData(rows);

      // If crumbs only has the player, attempt to extract hierarchy from bet history
      if (Array.isArray(rows) && rows.length > 0) {
        const first = rows.find(
          (r: any) =>
            (Array.isArray(r.userId?.whoAdd) && r.userId.whoAdd.length > 0) ||
            (Array.isArray(r.userId?.uperLineInfo) &&
              r.userId.uperLineInfo.length > 0) ||
            (Array.isArray(r.whoAdd) && r.whoAdd.length > 0)
        );
        if (first) {
          const upline =
            first.userId?.whoAdd ||
            first.userId?.uperLineInfo ||
            first.whoAdd;
          if (Array.isArray(upline) && upline.length > 0) {
            setCrumbs((prev) => {
              if (prev.length <= 1) {
                return buildBreadcrumbChain(upline, {
                  _id: userId,
                  user_name: userNameParam,
                  agent_level: agentLevelParam || "PL",
                });
              }
              return prev;
            });
          }
        }
      }
    } catch (err: any) {
      setPageData([]);
      if (err?.response?.data?.statusCode === 401) {
        Logout();
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getPageData("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const getProfit = (item: HistoryRow) => {
    const { tType, betSide, profit, stake, exposure } = item;
    const settled =
      tType === "win" ||
      tType === "lost" ||
      tType === "cancel" ||
      tType === "draw";
    // Live matched bets are not settled yet — same as the Top 20 list.
    if (betStatus === "unmatched" || !settled) {
      return { type: "", display: "-/-" };
    }
    if (tType === "cancel" || tType === "draw") return fmtPL(0, false);
    if (tType === "win") {
      if (betSide === "yes" || betSide === "back") return fmtPL(profit, false);
      return fmtPL(stake, false);
    }
    if (tType === "lost") {
      if (betSide === "yes" || betSide === "back") return fmtPL(exposure, true);
      return fmtPL(profit, true);
    }
    return { type: "", display: "-/-" };
  };

  return (
    <div style={{ display: "flex", justifyContent: "center", background: "#fff", height: "100vh" }}>
      <div className="risk-player-bets-page">
        <div className="risk-player-bets-head">
          <div />
          <button
            type="button"
            className="risk-bet-details-close"
            onClick={() => window.close()}
          >
            <span aria-hidden="true">×</span> Close
          </button>
        </div>

        <div className="risk-player-bets-crumbs" style={{ paddingLeft: "12px", paddingRight: "12px" }}>
          <ul className="agentlist d_flex_fix">
            {crumbs.map((item, index) => (
              <li key={`${item._id}-${index}`}>
                <a href="javascript:void(0)" className="agent-bread-cum sub-agent" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span
                    className="blue-bg text-color-white"
                    style={{
                      backgroundColor: AGENT_LEVEL_BG[item.agent_level] || "#568BC8", marginTop: "0px",
                    }}
                  >
                    {item.agent_level || "PL"}
                  </span>
                  <p style={{ fontSize: "14px", color: "#000" }}>{item.user_name}</p>
                </a>
                {index < crumbs.length - 1 && (
                  <img src="/images/arrow-right2.png" alt=">" />
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className="risk-player-main-tabs" style={{ paddingLeft: "12px", paddingRight: "12px" }}>
          <button
            type="button"
            className={mainTab === "bethistory" ? "active" : ""}
            onClick={() => setMainTab("bethistory")}
          >
            Bets History
          </button>
          <button
            type="button"
            className={mainTab === "profitloss" ? "active" : ""}
            onClick={() => setMainTab("profitloss")}
          >
            Profit & Loss
          </button>
        </div>

        {mainTab === "bethistory" ? (
          <div className="risk-player-bets-body">
            <ul className="nav nav-tabs risk-player-bet-tabs" role="tablist">
              {BET_TABS.map((tabItem) => (
                <li className="nav-item" key={tabItem.key}>
                  <a
                    href="#"
                    role="tab"
                    className={`${currentTab === tabItem.key ? "active" : ""} nav-link`}
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentTab(tabItem.key);
                      getPageData("", tabItem.key);
                    }}
                  >
                    {tabItem.label}
                  </a>
                </li>
              ))}
            </ul>

            <div className="function-wrap light-grey-bg risk-player-filters" style={{ display: 'block' }}>
                <ul className="inputlist">
                  <li>
                    <label>Bet Status: </label>
                  </li>
                  <li>
                    <select
                      value={betStatus}
                      onChange={(e) => {
                        setBetStatus(e.target.value);
                        getPageData("", currentTab, e.target.value);
                      }}
                    >
                       <option value="unmatched">Unmatched</option>
                      <option value="matched">Matched</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="voided">Voided</option>
                    </select>
                  </li>
                <li>
                  <label>Period</label>
                </li>
                <li className="date-time-group">
                  <input
                    className="form-control1"
                    type="date"
                    value={moment(startDate).format("YYYY-MM-DD")}
                    onChange={(e) => {
                      const newDate = moment(
                        `${e.target.value} ${startTime}`,
                        "YYYY-MM-DD HH:mm"
                      ).toDate();
                      setStartDate(newDate);
                      setFilterTab("date");
                    }}
                  />
                  <input
                    className="form-control1 time-input"
                    type="text"
                    maxLength={5}
                    value={startTime}
                    onChange={(e) => {
                      const value = e.target.value;
                      setStartTime(value);
                      const newDate = moment(
                        `${moment(startDate).format("YYYY-MM-DD")} ${value}`,
                        "YYYY-MM-DD HH:mm"
                      ).toDate();
                      if (newDate instanceof Date && !isNaN(newDate.getTime())) {
                        setStartDate(newDate);
                      }
                      setFilterTab("date");
                    }}
                  />
                  <span> to </span>
                  <input
                    className="form-control1"
                    type="date"
                    value={moment(endDate).format("YYYY-MM-DD")}
                    onChange={(e) => {
                      const newDate = moment(
                        `${e.target.value} ${endTime}`,
                        "YYYY-MM-DD HH:mm"
                      ).toDate();
                      setEndDate(newDate);
                      setFilterTab("date");
                    }}
                  />
                  <input
                    className="form-control1 time-input"
                    type="text"
                    maxLength={5}
                    value={endTime}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEndTime(value);
                      const newDate = moment(
                        `${moment(endDate).format("YYYY-MM-DD")} ${value}`,
                        "YYYY-MM-DD HH:mm"
                      ).toDate();
                      if (newDate instanceof Date && !isNaN(newDate.getTime())) {
                        setEndDate(newDate);
                      }
                      setFilterTab("date");
                    }}
                  />
                </li>
              </ul>
              <ul className="inputlist history-filter-btns">
                <li>
                  <button
                    type="button"
                    className="btn btn-default-customize"
                    onClick={() => {
                      setFilterTab("today");
                      getPageData("today");
                    }}
                  >
                    Just For Today
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="btn btn-default-customize"
                    onClick={() => {
                      setFilterTab("yesterday");
                      getPageData("yesterday");
                    }}
                  >
                    From Yesterday
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="btn btn-default-customize get-history"
                    onClick={() => getPageData("")}
                  >
                    Get History
                  </button>
                </li>
              </ul>
            </div>

            <p>Betting History enables you to review the bets you have placed.</p>
            <p style={{ marginBottom: "10px" }}>
              Specify the time period during which your bets were placed, the type
              of markets on which the bets were placed, and the sport.
            </p>
            <p style={{ marginBottom: "10px" }}>Betting History is available online for the past 30 days.</p>

            <div className="table-responsive">
              {loading ? (
                <Loader />
              ) : (
                <table className="table01 margin-table bh-table">
                  <caption>
                    {betStatus === "matched"
                      ? "Matched"
                      : betStatus.charAt(0).toUpperCase() + betStatus.slice(1)}
                  </caption>
                  <thead>
                    <tr>
                      <th style={{ width: "9%" }}>Bet ID</th>
                      <th style={{ width: "9%" }}>PL ID</th>
                      <th>Market</th>
                      <th style={{ whiteSpace: "nowrap", width: "12%" }} className="align-R">Selection</th>
                      <th style={{ width: "4%" }} className="align-R">Type</th>
                      <th style={{ width: "8%" }} className="align-R">Bet placed</th>
                      <th className="col-num align-R" style={{ width: "7%" }}>Odds req.</th>
                      <th className="col-num align-R" style={{ width: "8%" }}>Stake</th>
                      <th className="col-num align-R" style={{ width: "8%" }}>Avg. odds matched</th>
                      <th className="col-num align-R" style={{ width: "10%" }}>Profit/Loss</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageData.length > 0 ? (
                      pageData.map((item) => {
                        const pl = getProfit(item);
                        return (
                          <tr key={item._id || String(item.betId)}>
                            <td>{item.betId || item.betDisplayId}</td>
                            <td>{item.plId || userNameParam || "--"}</td>
                            <td>
                              {item.type} ▸ <strong>{item.name}</strong> ▸ {item.betType}
                            </td>
                            <td className="align-R">
                              {item.betType === "premium"
                                ? `${item.selection} / ${item.subSelection || ""}`
                                : item.selection}
                            </td>
                            <td style={{ textTransform: "uppercase" }} className="align-R">
                              <span
                                style={{
                                  padding: 6,
                                  borderRadius: 3,
                                  background:
                                    item.betSide === "back" ||
                                      item.betSide === "yes"
                                      ? "#72bbef"
                                      : "#faa9ba",
                                }}
                              >
                                {item.betSide}
                              </span>
                            </td>
                            <td className="align-R">
                              {moment(item.createdAt).format("DD-MM-YYYY hh:mm A")}
                            </td>
                            <td className="col-num align-R">{item.oddsUp}</td>
                            <td className="col-num align-R">{item.stake}</td>
                            <td className="col-num align-R">{item.oddsUp}</td>
                            <td className={`col-num ${pl.type} align-R`}>{pl.display}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={10}>
                          <p>You have no bets in this time period.</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        ) : (
          <div className="risk-player-bets-body">
            <BettingProfitLoss
              userId={userId}
              TYPE={agentLevelParam === "PL" || agentLevelParam === "CL" ? "player" : "agent"}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default RiskPlayerBetsPopup;

export function openRiskPlayerBetsWindow(options: {
  userId: string;
  userName?: string;
  agentLevel?: string;
  whoAdd?: any[];
}) {
  const params = new URLSearchParams({
    userId: resolvePlayerId(options.userId),
  });
  if (options.userName) params.set("userName", options.userName);
  if (options.agentLevel) params.set("agentLevel", options.agentLevel);
  if (Array.isArray(options.whoAdd) && options.whoAdd.length > 0) {
    try {
      params.set("whoAdd", JSON.stringify(options.whoAdd));
    } catch {
      // ignore
    }
  }

  const width = 1100;
  const height = 720;
  const left = window.screenX || window.screenLeft || 0;
  const top = window.screenY || window.screenTop || 0;
  const features = [
    `width=${width}`,
    `height=${height}`,
    `left=${left}`,
    `top=${top}`,
    "resizable=yes",
    "scrollbars=yes",
    "toolbar=no",
    "menubar=no",
    "location=no",
    "status=no",
  ].join(",");

  const popup = window.open(
    `/risk-player-bets?${params.toString()}`,
    `RiskPlayerBets_${options.userId}`,
    features
  );
  if (popup) popup.focus();
  return popup;
}
